import {
  BooleanIcon,
  getBooleanColors,
} from "@/components/ui/table/core/BooleanCell";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { ChevronDown } from "lucide-react";
import { useRef } from "react";
import { useEffect } from "react";
import { useState } from "react";

interface AssertionsCellProps {
  className?: string;
  prompt?: string;
  value: any;
}

export const AssertionsCell = ({
  className = "",
  prompt = "",
  value,
}: AssertionsCellProps) => {
  const assertions = Object.entries(
    typeof value === "object" ? value : { [prompt]: value },
  );
  const [offset, setOffset] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [showIndicator, setShowIndicator] = useState(false);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const handleScroll = () => {
      const nearBottom =
        element.scrollTop + element.clientHeight >= element.scrollHeight - 10;
      setShowIndicator(!nearBottom);
    };

    handleScroll();
    element.addEventListener("scroll", handleScroll);
    return () => element.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    const assertionsLength = assertions.length;

    if (!element || assertionsLength === 0) return;

    const divider = assertionsLength === 1 ? 2 : assertionsLength;

    setOffset(element.clientHeight / divider - 15 * assertionsLength);
  }, [assertions]);

  if (assertions.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center p-2">
        <span className="text-gray-500">
          No assertions were provided for this column.
        </span>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className={`grid h-full min-h-[50px] w-full overflow-y-auto ${className}`}
    >
      {assertions.map(([key, value]) => (
        <BasicTooltip
          key={key}
          content={key}
          disabled={!key}
          side="top"
          offset={-offset}
        >
          <span
            className={`${getBooleanColors(
              value as boolean,
            )} flex h-full min-h-[50px] items-center justify-center`}
          >
            <BooleanIcon value={value as boolean} />
          </span>
        </BasicTooltip>
      ))}
      <div
        className={`${
          showIndicator ? "opacity-100" : "opacity-0"
        } pointer-events-none absolute bottom-1 right-1 rounded-full border-gray-300 p-1 transition-opacity`}
      >
        <ChevronDown className="h-4 w-4 opacity-50" />
      </div>
    </div>
  );
};
